import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { geoMercator, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';

const TOPO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const countryData: Record<string, { name: string; nameAr: string; companies: number }> = {
  '682': { name: 'Saudi Arabia', nameAr: 'السعودية', companies: 258 },
  '784': { name: 'UAE', nameAr: 'الإمارات', companies: 179 },
  '634': { name: 'Qatar', nameAr: 'قطر', companies: 54 },
  '414': { name: 'Kuwait', nameAr: 'الكويت', companies: 184 },
  '048': { name: 'Bahrain', nameAr: 'البحرين', companies: 44 },
  '512': { name: 'Oman', nameAr: 'عمان', companies: 28 },
  '400': { name: 'Jordan', nameAr: 'الأردن', companies: 78 },
  '422': { name: 'Lebanon', nameAr: 'لبنان', companies: 10 },
  '275': { name: 'Palestine', nameAr: 'فلسطين', companies: 48 },
};

interface MiddleEastMapProps {
  isRTL?: boolean;
}

const MiddleEastMap = ({ isRTL = false }: MiddleEastMapProps) => {
  const [geoFeatures, setGeoFeatures] = useState<any[]>([]);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const width = 500;
  const height = 400;

  const coveredIds = useMemo(
    () =>
      new Set(
        Object.entries(countryData)
          .filter(([, d]) => d.companies > 0)
          .map(([id]) => id)
      ),
    []
  );

  const projection = useMemo(() => {
    const proj = geoMercator().translate([width / 2, height / 2]);
    const covered = geoFeatures.filter((f) => coveredIds.has(f.id));
    if (covered.length > 0) {
      proj.fitExtent(
        [
          [24, 24],
          [width - 24, height - 24],
        ],
        { type: 'FeatureCollection', features: covered } as any
      );
    } else {
      proj.center([47, 24]).scale(700);
    }
    return proj;
  }, [geoFeatures, coveredIds]);

  const pathGenerator = useMemo(() => geoPath().projection(projection), [projection]);

  useEffect(() => {
    fetch(TOPO_URL)
      .then((res) => res.json())
      .then((topology) => {
        const countries = feature(topology, topology.objects.countries) as any;
        setGeoFeatures(countries.features);
      })
      .catch(() => {});
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setTooltipPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);

  const hoveredData = hoveredId ? countryData[hoveredId] : null;

  return (
    <div className="relative" data-testid="middle-east-map" ref={containerRef}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        style={{ maxHeight: '400px' }}
      >
        {geoFeatures.map((geo, idx) => {
          const id = geo.id;
          if (!coveredIds.has(id)) return null;
          const isHovered = hoveredId === id;
          const d = pathGenerator(geo);

          if (!d) return null;

          return (
            <path
              key={`me-${id}-${idx}`}
              d={d}
              fill={isHovered ? 'rgba(208, 147, 16, 0.8)' : 'rgba(255, 255, 255, 0.25)'}
              stroke="rgba(255, 255, 255, 0.5)"
              strokeWidth={0.8}
              className="transition-colors duration-200 cursor-pointer"
              onMouseEnter={() => setHoveredId(id)}
              onMouseLeave={() => setHoveredId(null)}
              onMouseMove={handleMouseMove}
              data-testid={`map-country-${id}`}
            />
          );
        })}

        {geoFeatures.length > 0 &&
          Object.keys(countryData).map((id) => {
            const geo = geoFeatures.find((f) => f.id === id);
            if (!geo) return null;
            const centroid = pathGenerator.centroid(geo);
            if (!centroid || isNaN(centroid[0])) return null;
            return (
              <g key={`marker-${id}`}>
                <circle
                  cx={centroid[0]}
                  cy={centroid[1]}
                  r={5}
                  fill="rgba(208, 147, 16, 0.4)"
                  className="pointer-events-none"
                />
                <circle
                  cx={centroid[0]}
                  cy={centroid[1]}
                  r={2.5}
                  fill="rgba(208, 147, 16, 1)"
                  className="pointer-events-none"
                />
              </g>
            );
          })}
      </svg>

      {hoveredData && (
        <div
          className="absolute pointer-events-none z-10 bg-black/85 backdrop-blur-sm text-white px-4 py-2.5 rounded-lg shadow-xl border border-white/20 whitespace-nowrap"
          style={{
            left: Math.min(Math.max(tooltipPos.x, 60), width - 60),
            top: Math.max(tooltipPos.y - 60, 10),
            transform: 'translateX(-50%)',
          }}
          data-testid="map-tooltip"
        >
          <div className="font-semibold text-sm" data-testid="map-tooltip-name">
            {isRTL ? hoveredData.nameAr : hoveredData.name}
          </div>
          {hoveredData.companies > 0 ? (
            <div className="text-accent text-xs font-bold mt-0.5" data-testid="map-tooltip-companies">
              {hoveredData.companies} {isRTL ? 'شركة مدرجة' : 'listed companies'}
            </div>
          ) : (
            <div className="text-white/50 text-xs mt-0.5" data-testid="map-tooltip-companies">
              {isRTL ? 'قريبًا' : 'Coming soon'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MiddleEastMap;
